import React, { useState, useEffect } from 'react';
import { API_URL } from '../../services/api';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import {
  TrendingUp, Package, Clock, AlertTriangle, CheckCircle,
  RefreshCw, Layers, Users, Gem, Calendar
} from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 });
const fmtDH = (n) => fmt(n) + ' DH';

function KpiCard({ icon: Icon, label, value, sub, color = 'blue', alert = false }) {
  const colors = {
    blue:   'bg-blue-50 text-blue-700 border-blue-200',
    green:  'bg-green-50 text-green-700 border-green-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    red:    'bg-red-50 text-red-700 border-red-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  };
  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 ${alert ? 'bg-red-50 border-red-300' : colors[color] || colors.blue}`}>
      <div className={`mt-0.5 p-2 rounded-lg ${alert ? 'bg-red-100' : 'bg-white bg-opacity-70'}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-medium opacity-70 mb-0.5">{label}</div>
        <div className="text-xl font-bold leading-tight">{value}</div>
        {sub && <div className="text-xs opacity-60 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function DelaiTag({ jours }) {
  if (jours === null || jours === undefined) return <span className="text-gray-400 text-xs">—</span>;
  if (jours < 0) return <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">🔴 {Math.abs(jours)}j retard</span>;
  if (jours === 0) return <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">🔴 Aujourd'hui</span>;
  if (jours <= 2) return <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">🟠 J-{jours}</span>;
  if (jours <= 7) return <span className="text-xs font-bold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">🟡 J-{jours}</span>;
  return <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">📅 {jours}j</span>;
}

export default function PilotageProduction() {
  const [kpis, setKpis] = useState(null);
  const [mensuel, setMensuel] = useState([]);
  const [pipeline, setPipeline] = useState({ par_statut: [], derniers: [] });
  const [ofsEnCours, setOfsEnCours] = useState([]);
  const [matieres, setMatieres] = useState({ par_ouvrier: [], historique: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [kpisRes, mensuelRes, pipelineRes, ofsRes, matieresRes] = await Promise.all([
        fetch(`${API_URL}/dashboard-production/kpis`),
        fetch(`${API_URL}/dashboard-production/production-mensuelle`),
        fetch(`${API_URL}/dashboard-production/devis-pipeline`),
        fetch(`${API_URL}/dashboard-production/ofs-en-cours`),
        fetch(`${API_URL}/dashboard-production/matieres-consommees`),
      ]);
      if (kpisRes.ok) setKpis(await kpisRes.json());
      if (mensuelRes.ok) setMensuel(await mensuelRes.json());
      if (pipelineRes.ok) setPipeline(await pipelineRes.json());
      if (ofsRes.ok) setOfsEnCours(await ofsRes.json());
      if (matieresRes.ok) setMatieres(await matieresRes.json());
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-gray-400">
      <RefreshCw className="animate-spin mr-2" size={20} /> Chargement...
    </div>
  );

  const statutLabels = {
    en_creation: 'En création', EN_ATTENTE: 'En attente', valide: 'Validé',
    EN_PRODUCTION: 'En production', termine: 'Terminé', livre: 'Livré', annule: 'Annulé'
  };
  const statutColors = {
    en_creation: '#94a3b8', EN_ATTENTE: '#fbbf24', valide: '#60a5fa',
    EN_PRODUCTION: '#f97316', termine: '#22c55e', livre: '#8b5cf6', annule: '#ef4444'
  };

  return (
    <div className="space-y-6 pb-10">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Pilotage Production</h2>
          <p className="text-sm text-gray-500 mt-0.5">Vue d'ensemble — données en temps réel</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border rounded-lg hover:bg-gray-50 text-gray-600">
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {/* KPIs principaux */}
      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard icon={TrendingUp} label="CA ce mois" value={fmtDH(kpis.ca_mois.montant)}
            sub={`${kpis.ca_mois.nb_termines} OF terminés`} color="green" />
          <KpiCard icon={TrendingUp} label="CA cette année" value={fmtDH(kpis.ca_ytd.montant)}
            sub={`${kpis.ca_ytd.nb} OFs`} color="blue" />
          <KpiCard icon={Package} label="OFs actifs" value={kpis.ofs.total}
            sub={`${kpis.ofs.en_cours} en cours · ${kpis.ofs.en_attente} en attente`} color="orange" />
          <KpiCard icon={AlertTriangle} label="En retard" value={kpis.ofs.en_retard}
            sub="OFs dépassé délai" color={kpis.ofs.en_retard > 0 ? 'red' : 'green'}
            alert={kpis.ofs.en_retard > 0} />
          <KpiCard icon={Layers} label="Devis en cours" value={kpis.devis_en_cours.nb}
            sub={`CA prévu : ${fmtDH(kpis.devis_en_cours.ca_prevu)}`} color="purple" />
          <KpiCard icon={CheckCircle} label="Taux acceptation" value={`${kpis.taux_acceptation}%`}
            sub="3 derniers mois" color={kpis.taux_acceptation >= 60 ? 'green' : 'yellow'} />
        </div>
      )}

      {/* Production client vs interne */}
      {kpis && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs font-semibold text-gray-500 mb-3">Production client vs interne</div>
            <div className="flex gap-4">
              <div className="flex-1 text-center">
                <div className="text-2xl font-bold text-blue-600">{kpis.ofs.client}</div>
                <div className="text-xs text-gray-500 mt-1">Commandes client</div>
                <div className="mt-2 h-2 bg-blue-200 rounded-full">
                  <div className="h-2 bg-blue-500 rounded-full"
                    style={{width: kpis.ofs.total > 0 ? `${kpis.ofs.client/kpis.ofs.total*100}%` : '0%'}} />
                </div>
              </div>
              <div className="w-px bg-gray-100" />
              <div className="flex-1 text-center">
                <div className="text-2xl font-bold text-orange-500">{kpis.ofs.interne}</div>
                <div className="text-xs text-gray-500 mt-1">Production interne</div>
                <div className="mt-2 h-2 bg-orange-200 rounded-full">
                  <div className="h-2 bg-orange-400 rounded-full"
                    style={{width: kpis.ofs.total > 0 ? `${kpis.ofs.interne/kpis.ofs.total*100}%` : '0%'}} />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs font-semibold text-gray-500 mb-3">Or consommé — ouvriers ce mois</div>
            <div className="text-2xl font-bold text-yellow-600">{fmt(kpis.or_consomme_mois)} g</div>
            <div className="text-xs text-gray-400 mt-1">Sorties coffres ouvriers</div>
            {matieres.par_ouvrier.filter(o => o.grammes > 0).slice(0,3).map((o, i) => (
              <div key={i} className="flex justify-between items-center mt-2 text-sm">
                <span className="text-gray-700">{o.prenom} {o.nom}</span>
                <span className="font-semibold text-yellow-700">{fmt(o.grammes)}g</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* CA mensuel */}
        <div className="bg-white rounded-xl border p-4">
          <div className="text-sm font-semibold text-gray-700 mb-4">CA production — 12 derniers mois</div>
          {mensuel.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={mensuel} margin={{top:0,right:0,left:-10,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mois" tick={{fontSize:11}} />
                <YAxis tick={{fontSize:11}} tickFormatter={v => fmt(v)} />
                <Tooltip formatter={(v) => [fmtDH(v), 'CA']} />
                <Bar dataKey="ca" fill="#f97316" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Pas encore de données</div>}
        </div>

        {/* Pipeline devis */}
        <div className="bg-white rounded-xl border p-4">
          <div className="text-sm font-semibold text-gray-700 mb-4">Pipeline devis — 6 derniers mois</div>
          {pipeline.par_statut.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={pipeline.par_statut} margin={{top:0,right:0,left:-10,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="statut" tickFormatter={s => (statutLabels[s] || s).substring(0,8)} tick={{fontSize:10}} />
                <YAxis tick={{fontSize:11}} />
                <Tooltip labelFormatter={s => statutLabels[s] || s} formatter={(v,n) => [v, n === 'nb' ? 'Nombre' : 'CA prévu']} />
                <Bar dataKey="nb" radius={[4,4,0,0]}>
                  {pipeline.par_statut.map((entry, i) => (
                    <Cell key={i} fill={statutColors[entry.statut] || '#94a3b8'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Pas encore de données</div>}
        </div>

      </div>

      {/* Tableau OFs en cours */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700">OFs actifs — par priorité délai</div>
          <span className="text-xs text-gray-400">{ofsEnCours.length} ordres</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                <th className="px-4 py-2 text-left font-medium">Référence</th>
                <th className="px-4 py-2 text-left font-medium">Client</th>
                <th className="px-4 py-2 text-left font-medium">Type</th>
                <th className="px-4 py-2 text-left font-medium">Avancement</th>
                <th className="px-4 py-2 text-left font-medium">Livraison</th>
                <th className="px-4 py-2 text-left font-medium">Statut</th>
              </tr>
            </thead>
            <tbody>
              {ofsEnCours.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Aucun OF actif</td></tr>
              )}
              {ofsEnCours.map((of, i) => {
                const pct = of.nb_etapes > 0 ? Math.round(of.nb_terminees / of.nb_etapes * 100) : 0;
                return (
                  <tr key={i} className={`border-t ${of.jours_restants !== null && of.jours_restants < 0 ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-mono font-semibold text-gray-900">{of.ref_article || of.numero_ordre}</div>
                      <div className="text-xs text-gray-400">{of.numero_ordre}</div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">{of.client_nom || '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        of.type_creation === 'CLIENT' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {of.type_creation === 'CLIENT' ? 'Client' : 'Interne'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-orange-400'}`}
                            style={{width: `${pct}%`}} />
                        </div>
                        <span className="text-xs font-semibold text-gray-600">{pct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {of.date_livraison_prevue && (
                        <div className="text-xs text-gray-500 mb-1">
                          {new Date(of.date_livraison_prevue).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                      <DelaiTag jours={of.jours_restants} />
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs text-gray-500 capitalize">{of.statut?.replace('_',' ')}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Derniers devis */}
      {pipeline.derniers?.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b">
            <div className="text-sm font-semibold text-gray-700">Devis actifs récents</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <th className="px-4 py-2 text-left font-medium">Numéro</th>
                  <th className="px-4 py-2 text-left font-medium">Client</th>
                  <th className="px-4 py-2 text-left font-medium">Type</th>
                  <th className="px-4 py-2 text-left font-medium">Statut</th>
                  <th className="px-4 py-2 text-right font-medium">Prix</th>
                  <th className="px-4 py-2 text-left font-medium">Date création</th>
                </tr>
              </thead>
              <tbody>
                {pipeline.derniers.map((d, i) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-mono font-semibold text-gray-900">{d.numero_devis}</td>
                    <td className="px-4 py-2.5 text-gray-600">{d.client_nom || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-500 capitalize">{d.type_bijou}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{
                          background: (statutColors[d.statut] || '#94a3b8') + '22',
                          color: statutColors[d.statut] || '#64748b'
                        }}>
                        {statutLabels[d.statut] || d.statut}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-orange-600">
                      {d.prix_vente_final ? fmtDH(d.prix_vente_final) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-400 text-xs">
                      {d.date_creation ? new Date(d.date_creation).toLocaleDateString('fr-FR') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
